import {
    EmailMarketingSenderUpdatePayload,
    fetchEmailMarketingSenderThunk,
    refreshEmailMarketingSenderThunk,
    updateEmailMarketingSenderThunk
} from '@spike/email-marketing-action';
import { MarketplaceEmailSender } from '@spike/email-marketing-model';
import { useApiClientWrapper } from 'hooks';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

interface UseEmailSenderData {
    /** Latest payload from the BE; null before first fetch completes. */
    data: MarketplaceEmailSender | null;
    /** True during the initial GET — drives the full-tab spinner. */
    loading: boolean;
    /** True during update / refresh requests — drives per-button busy states. */
    saving: boolean;
    /** Flat array of human-readable error strings from the latest failed save. */
    errors: string[];

    /** Derived booleans — keep the state-switch in the component declarative. */
    isUnconfigured: boolean;
    isPending: boolean;
    isVerified: boolean;
    isFailed: boolean;

    /** Imperative dispatchers — return promises that resolve to the post-call sender (or null on failure). */
    fetchSender: () => Promise<MarketplaceEmailSender | null>;
    updateSender: (payload: EmailMarketingSenderUpdatePayload) => Promise<MarketplaceEmailSender | null>;
    refreshSender: () => Promise<MarketplaceEmailSender | null>;
}

/**
 * Single source of truth for everything Email Sender related — the slice
 * selectors, the dispatchers, and the derived state booleans. Pulled into a
 * hook so the component renders stay declarative (`if (isPending) ... else if
 * (isVerified) ...`) and the dispatchers don't need their own useCallback
 * boilerplate in every consumer.
 *
 * The derived booleans treat `enabled === false` as `isUnconfigured` even when
 * the status string is something else — for the FE state machine these are
 * indistinguishable (both end up at the "set up custom sender" CTA).
 */
export const useEmailSenderData = (): UseEmailSenderData => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const data = useSelector((state: RootState) => state.emailMarketingSender.data);
    const loading = useSelector((state: RootState) => state.emailMarketingSender.loading);
    const saving = useSelector((state: RootState) => state.emailMarketingSender.saving);
    const errors = useSelector((state: RootState) => state.emailMarketingSender.errors);

    const isEnabled = data?.enabled === true;
    const status = data?.status ?? 'unconfigured';
    const isUnconfigured = !data || !isEnabled || status === 'unconfigured';
    const isPending = isEnabled && status === 'pending';
    const isVerified = isEnabled && status === 'verified';
    const isFailed = isEnabled && status === 'failed';

    const fetchSender = useCallback(
        () =>
            dispatch(
                fetchEmailMarketingSenderThunk(apiClientWrapper)
            ) as unknown as Promise<MarketplaceEmailSender | null>,
        [dispatch, apiClientWrapper]
    );

    const updateSender = useCallback(
        (payload: EmailMarketingSenderUpdatePayload) =>
            dispatch(
                updateEmailMarketingSenderThunk(apiClientWrapper, payload)
            ) as unknown as Promise<MarketplaceEmailSender | null>,
        [dispatch, apiClientWrapper]
    );

    const refreshSender = useCallback(
        () =>
            dispatch(
                refreshEmailMarketingSenderThunk(apiClientWrapper)
            ) as unknown as Promise<MarketplaceEmailSender | null>,
        [dispatch, apiClientWrapper]
    );

    return {
        data,
        loading,
        saving,
        errors,
        isUnconfigured,
        isPending,
        isVerified,
        isFailed,
        fetchSender,
        updateSender,
        refreshSender
    };
};
